import { FC, useEffect, useState } from "react";
import { Modal } from "antd";
import { useTranslation } from "react-i18next";
import LoadingSpinner from "src/components/reusable/LoadingSpinner";
import { autopopulateCampaignData } from "src/services/EngagementAPIs";
import type { AutopopulateResponse } from "../../EngagementInterfaces";
import "../Styles/CampaignViewModal.scss";

export interface CampaignViewSummary {
  campaignId: string;
  name: string;
  source: string;
  sourceLabel: string;
  sourceVariant: string;
  /** Saved HTML of a draft; when set, nothing is fetched. */
  html?: string;
}

interface CampaignViewModalProps {
  campaign: CampaignViewSummary | null;
  onClose: () => void;
}

type PreviewState =
  | { status: "loading" }
  | { status: "ready"; html: string }
  | { status: "unavailable" };

const CampaignViewModal: FC<CampaignViewModalProps> = ({ campaign, onClose }) => {
  const { t } = useTranslation();
  const [preview, setPreview] = useState<PreviewState>({ status: "loading" });

  const campaignId = campaign?.campaignId;
  const isEmail = campaign?.source.toLowerCase() === "email";
  const draftHtml = campaign?.html;

  useEffect(() => {
    if (draftHtml) {
      setPreview({ status: "ready", html: draftHtml });
      return;
    }
    if (!campaignId || !isEmail) {
      setPreview({ status: "unavailable" });
      return;
    }
    const tenantId = localStorage.getItem("tenant_id");
    const apiKey = localStorage.getItem("api_key");
    if (!tenantId || !apiKey) {
      setPreview({ status: "unavailable" });
      return;
    }

    let cancelled = false;
    setPreview({ status: "loading" });
    autopopulateCampaignData(Number(tenantId), campaignId, apiKey)
      .then((data: AutopopulateResponse) => {
        if (cancelled) {return;}
        setPreview(
          data.status === "success" && data.email_html
            ? { status: "ready", html: data.email_html }
            : { status: "unavailable" }
        );
      })
      .catch(() => {
        if (!cancelled) {setPreview({ status: "unavailable" });}
      });

    return () => {
      cancelled = true;
    };
  }, [campaignId, isEmail, draftHtml]);

  return (
    <Modal
      open={Boolean(campaign)}
      onCancel={onClose}
      width={900}
      centered
      destroyOnClose
      className="campaign-view-modal"
      title={
        <div className="campaign-view__heading">
          <h2 className="campaign-view__title">{campaign?.name}</h2>
          {campaign?.sourceLabel ? (
            <span
              className={`campaign-view__source campaign-view__source--${campaign.sourceVariant}`}
            >
              {campaign.sourceLabel}
            </span>
          ) : null}
        </div>
      }
      footer={
        <div className="campaign-view__footer">
          <button type="button" className="button button--outline" onClick={onClose}>
            {t("campaignDashboard.back")}
          </button>
          <button type="button" className="button button--primary" disabled>
            {t("campaignDashboard.edit")}
          </button>
        </div>
      }
    >
      <div className="campaign-view__preview">
        {preview.status === "loading" ? <LoadingSpinner /> : null}
        {preview.status === "ready" ? (
          <iframe
            className="campaign-view__frame"
            title={t("campaignDashboard.emailPreview")}
            sandbox=""
            srcDoc={preview.html}
          />
        ) : null}
        {preview.status === "unavailable" ? (
          <p className="campaign-view__empty">{t("campaignDashboard.previewUnavailable")}</p>
        ) : null}
      </div>
    </Modal>
  );
};

export default CampaignViewModal;
